import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import Hidden from "@insite/mobius/Hidden";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { formatDateByYearMonthDay } from "@ucs/Helpers/Formatting";
import UCSColors from "@ucs/Utils/UCSColors";
import React, { FC, useContext } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    return {
        language: state.context.session.language,
    };
};

export interface OrderDetailsOrderInformationStyles {
    container?: InjectableCss;
    shippingInfoSection?: InjectableCss;
    shippingInfoGroup?: InjectableCss;
    shippingInfoHeading?: TypographyPresentationProps;
    shippingInfoLabel?: TypographyPresentationProps;
    shippingInfoText?: TypographyPresentationProps;
}

export const orderInformationStyles: OrderDetailsOrderInformationStyles = {
    container: {
        css: css`
            width: 100%;
            padding: 20px;
            background-color: ${UCSColors.gray10};
            box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.05);
            display: flex;
            flex-direction: column;
            gap: 16px;
            margin-bottom: 10px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        padding: 20px 10px;
                        gap: 0;
                    `,
                    css`
                        gap: 0;
                    `,
                ]);
            }}
        `,
    },
    shippingInfoHeading: {
        variant: "h3",
    },
    shippingInfoSection: {
        css: css`
            width: 100%;
            display: grid;
            grid-template-columns: repeat(4, calc(25% - 6px));
            gap: 16px 10px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        grid-template-columns: 100%;
                        gap: 12px 0;
                    `,
                    css`
                        grid-template-columns: repeat(2, calc(50% - 5px));
                    `,
                    css`
                        grid-template-columns: repeat(2, calc(50% - 5px));
                    `,
                ]);
            }}
        `,
    },
    shippingInfoGroup: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 8px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        display: grid;
                        grid-template-columns: repeat(2, calc(50% - 5px));
                        align-items: center;
                        gap: 10px;
                    `,
                ]);
            }}
        `,
    },
    shippingInfoLabel: {
        size: 13,
        weight: 700,
        color: UCSColors.gray04,
        transform: "uppercase",
        css: css`
            line-height: 16px;
            letter-spacing: 0.1em;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            font-size: 12px;
                        `,
                    ],
                    "max",
                );
            }}
        `,
    },
    shippingInfoText: {
        size: 18,
        weight: 400,
        color: UCSColors.gray03,
        css: css`
            line-height: 24px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            font-size: 17px;
                        `,
                    ],
                    "max",
                );
            }}
        `,
    },
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

const styles = orderInformationStyles;

const Separator = styled.div`
    height: 1px;
    width: 100%;
    margin: 24px 0;
    background-color: ${UCSColors.gray07};
`;

const OrderDetailsOrderInformation: FC<Props> = props => {
    const { value: order } = useContext(OrderStateContext);
    const { language } = props;
    const estimatedShipDate = order?.properties.estimatedShipDate ? order?.properties.estimatedShipDate : "";
    const estimatedShippingDate = new Date(estimatedShipDate);

    if (!order) {
        return null;
    }
    console.log(order);
    return (
        <StyledWrapper {...styles.container}>
            <Typography {...styles.shippingInfoHeading}>{translate("Shipping Information")}</Typography>
            <Hidden above="sm">
                <Separator />
            </Hidden>
            <StyledWrapper {...styles.shippingInfoSection}>
                <StyledWrapper {...styles.shippingInfoGroup}>
                    <Typography {...styles.shippingInfoLabel}>{translate("Status")}</Typography>
                    <Typography {...styles.shippingInfoText}>{order.status}</Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.shippingInfoGroup}>
                    <Typography {...styles.shippingInfoLabel}>{translate("Order Date")}</Typography>
                    <Typography {...styles.shippingInfoText}>
                        {formatDateByYearMonthDay(new Date(order.orderDate as Date))}
                    </Typography>
                </StyledWrapper>
                {(order.requestedDeliveryDateDisplay || order.properties?.nextAvailableTruck) && (
                    <StyledWrapper {...styles.shippingInfoGroup}>
                        <Typography {...styles.shippingInfoLabel}>{translate("Date Requested")}</Typography>
                        <Typography {...styles.shippingInfoText}>
                            {order.properties.nextAvailableTruck && order.properties.nextAvailableTruck === "1" ? (
                                <Typography {...styles.shippingInfoText}>
                                    {translate("Next available truck")}
                                </Typography>
                            ) : (
                                <LocalizedDateTime dateTime={order.requestedDeliveryDateDisplay} />
                            )}
                        </Typography>
                    </StyledWrapper>
                )}
                <StyledWrapper {...styles.shippingInfoGroup}>
                    <Typography {...styles.shippingInfoLabel}>{translate("Estimated Ship Date")}</Typography>
                    {order.properties?.estimatedShipDate ? (
                        <Typography {...styles.shippingInfoText}>
                            {formatDateByYearMonthDay(new Date(estimatedShippingDate))}
                        </Typography>
                    ) : (
                        <Typography {...styles.shippingInfoText}>
                            {translate("To be confirmed")}
                        </Typography>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(OrderDetailsOrderInformation),
    definition: {
        displayName: "Order Information",
        allowedContexts: ["OrderDetailsPage"],
        group: "Order Details",
    },
};

export default widgetModule;
